import type { PaymentOption } from "@/types";

/**
 * Source of truth: a tenure is subvention-eligible only when the backend
 * has applied subvention for that option (subvention.applied === true).
 */
export function isSubventionEligible(option: PaymentOption): boolean {
  return option.subvention?.applied === true;
}

/**
 * Savings for display: applied subvention amount plus any waived fee.
 */
export function getSubventionSavings(option: PaymentOption): {
  amount: number;
  currency: string;
} {
  if (!isSubventionEligible(option)) {
    return {
      amount: 0,
      currency: option.totalToBeRepaidAmount?.currency ?? "",
    };
  }
  const applied = option.subvention?.appliedTotalAmount?.amount ?? 0;
  const waived = option.waivedOffFee?.amount ?? 0;
  const currency =
    option.subvention?.appliedTotalAmount?.currency ??
    option.waivedOffFee?.currency ??
    option.totalToBeRepaidAmount?.currency ??
    "";
  return { amount: applied + waived, currency };
}

function hasSubventionFeeWaiver(option: PaymentOption): boolean {
  if (!isSubventionEligible(option)) return false;
  if (option.isFeeWaivedOff === true) return true;
  return (option.waivedOffFee?.amount ?? 0) > 0;
}

/**
 * CXO-2638: plan-details fee strikethrough uses total_customer_fee_amount only.
 * When total_customer_fee_amount is 0, do not show strikethrough (CXO-2701).
 */
export function shouldShowSubventionFeeStrikethrough(
  option: PaymentOption,
): boolean {
  if (!hasSubventionFeeWaiver(option)) return false;
  return (option.totalCustomerFeeAmount?.amount ?? 0) > 0;
}

/** Strikethrough amount for subvented plan-details fee row. */
export function getSubventionFeeStrikethroughAmount(
  option: PaymentOption,
): number {
  return option.totalCustomerFeeAmount?.amount ?? 0;
}

/** Green fee amount after waiver (sum of remaining fee components). */
export function getSubventionFeeAfterWaiverAmount(option: PaymentOption): number {
  const processingFee = option.processingFeeAmount?.amount ?? 0;
  const termCost = option.termCostAmount?.amount ?? 0;
  return processingFee + termCost;
}

/**
 * Case 1: both processing fee and murabaha profit removed (total customer fee = 0).
 */
export function isSubventionZeroFees(option: PaymentOption): boolean {
  return (option.totalCustomerFeeAmount?.amount ?? 0) === 0;
}

/** i18n keys for the inline green subvention pill on the plan card (CXO-2701). */
export type SubventionBannerMessageKey =
  | "subvention.plan_is_free"
  | "subvention.zero_service_fee"
  | "subvention.zero_profit_fees"
  /** CPF-only waiver — interim copy; pending PO/design confirmation. */
  | "subvention.zero_processing_fee";

export function isUaeMarket(
  countryCode: string | null | undefined,
): boolean {
  const cc = (countryCode ?? "").toUpperCase();
  return cc === "AE" || cc === "ARE";
}

export function isZeroApr(option: PaymentOption): boolean {
  return (option.annualPercentageRate ?? 0) === 0;
}

/**
 * "Nice choice! No fees on this plan." — subvention on, APR exactly 0, all fee
 * components waived (UAE and KSA). Partial-fee messages use fee fields only.
 */
function qualifiesForPlanIsFreeBanner(option: PaymentOption): boolean {
  if (!isZeroApr(option)) return false;
  const totalFees = option.totalCustomerFeeAmount?.amount ?? 0;
  const afterWaiverFees = getSubventionFeeAfterWaiverAmount(option);
  return afterWaiverFees === 0 || totalFees === 0;
}

/**
 * Resolves green subvention pill copy from fee fields, with APR gate on plan_is_free.
 *
 * | APR | total fees | term cost | processing fee | Message (UAE)      | Message (other)   |
 * |-----|------------|-----------|----------------|--------------------|-------------------|
 * | 0   | *          | 0         | 0              | plan is free       | plan is free      |
 * | 0   | 0          | *         | *              | plan is free       | plan is free      |
 * | *   | >0         | 0         | >0             | 0 service fee      | 0 profit fees     |
 * | *   | >0         | >0        | 0              | 0 processing fees* | 0 processing fees*|
 *
 * *CPF-only row uses interim copy until product confirms final wording.
 * Returns null when subvention is not applied or the fee pattern is unclassified.
 */
export function getSubventionBannerMessageKey(
  option: PaymentOption,
  countryCode: string | null | undefined,
): SubventionBannerMessageKey | null {
  if (!isSubventionEligible(option)) {
    return null;
  }

  const processingFee = option.processingFeeAmount?.amount ?? 0;
  const termCost = option.termCostAmount?.amount ?? 0;

  if (qualifiesForPlanIsFreeBanner(option)) {
    return "subvention.plan_is_free";
  }

  if (termCost === 0 && processingFee > 0) {
    return isUaeMarket(countryCode)
      ? "subvention.zero_service_fee"
      : "subvention.zero_profit_fees";
  }

  if (processingFee === 0 && termCost > 0) {
    return "subvention.zero_processing_fee";
  }

  return null;
}

/**
 * When subvention fully waives all fees, the green pill (CXO-2701) replaces
 * the generic "No fees on this plan" fee line — showing both is repetitive.
 */
export function shouldSuppressFeeLineForSubventionBanner(
  messageKey: SubventionBannerMessageKey | null,
  option: PaymentOption,
): boolean {
  if (messageKey !== "subvention.plan_is_free") return false;
  return getSubventionFeeAfterWaiverAmount(option) === 0;
}

/**
 * Map tenure -> savings for subvention-eligible options only.
 */
export function buildSubventionDataMap(
  paymentOptions: PaymentOption[],
): Record<number, { amount: number; currency: string }> {
  return paymentOptions.reduce<
    Record<number, { amount: number; currency: string }>
  >((acc, option) => {
    if (!isSubventionEligible(option)) return acc;
    acc[option.tenure] = getSubventionSavings(option);
    return acc;
  }, {});
}

/**
 * Set of tenure numbers that are subvention-eligible (for per-tenure indicators).
 */
export function getSubventionEligibleTenures(
  paymentOptions: PaymentOption[],
): Set<number> {
  const set = new Set<number>();
  for (const option of paymentOptions) {
    if (isSubventionEligible(option)) set.add(option.tenure);
  }
  return set;
}
